import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiSearch, FiEdit3 } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { exportToExcel } from '../../utils/export';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import Loader from '../../components/common/Loader';
import SearchableSelect from '../../components/common/SearchableSelect';
import { today } from '../../utils/format';

export default function Inventory() {
  const [rows, setRows] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showAdj, setShowAdj] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adjForm, setAdjForm] = useState({ type: 'IN', article_no: '', name: '', unit: '', quantity: '', date: today(), notes: '' });
  const [adjErrors, setAdjErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.inventory.stock();
      const list = Array.isArray(data) ? data : data.stock ? (data.stock.data || data.stock) : data.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      showError('Failed to load stock', extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.inventory.units().then(({ data }) => {
      const list = Array.isArray(data) ? data : data.units || [];
      setUnits(list);
    }).catch(() => {});
  }, []);

  const setAdj = (field, value) => setAdjForm((p) => ({ ...p, [field]: value }));

  const openAdjust = () => {
    setAdjForm({ type: 'IN', article_no: '', name: '', unit: '', quantity: '', date: today(), notes: '' });
    setAdjErrors({});
    setShowAdj(true);
  };

  const handleAdjust = async () => {
    const errs = {};
    if (!adjForm.type) errs.type = 'Type is required';
    if (!adjForm.article_no) errs.article_no = 'Article no is required';
    if (!adjForm.name) errs.name = 'Name is required';
    if (!adjForm.unit) errs.unit = 'Unit is required';
    if (!adjForm.quantity || Number(adjForm.quantity) <= 0) errs.quantity = 'Valid quantity is required';
    if (!adjForm.date) errs.date = 'Date is required';
    if (Object.keys(errs).length) { setAdjErrors(errs); return; }

    setSaving(true);
    try {
      const { data } = await api.inventory.adjust({
        article_no: adjForm.article_no,
        name: adjForm.name,
        unit: adjForm.unit,
        type: adjForm.type,
        quantity: Number(adjForm.quantity),
        date: adjForm.date,
        notes: adjForm.notes || undefined,
      });
      toast('success', '', data.message || 'Stock adjusted');
      setShowAdj(false);
      load();
    } catch (err) {
      showError('Failed to adjust stock', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const cols = [
    { key: 'article_no', label: 'Article No', render: (r) => <b>{r.article_no || '-'}</b> },
    { key: 'name', label: 'Name' },
    { key: 'unit', label: 'Unit' },
    { key: 'total_in', label: 'Stock In', align: 'right', render: (r) => Number(r.total_in ?? 0).toLocaleString() },
    { key: 'total_out', label: 'Stock Out', align: 'right', render: (r) => Number(r.total_out ?? 0).toLocaleString() },
    { key: 'balance', label: 'Balance', align: 'right', render: (r) => {
      const bal = r.balance ?? r.stock ?? r.current_qty ?? 0;
      const neg = r.is_negative || Number(bal) < 0;
      return <b style={{ color: neg ? 'var(--danger)' : 'inherit' }}>{Number(bal).toLocaleString()}</b>;
    } },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Stock"
        subtitle="Current inventory stock levels."
        crumbs={[{ label: 'Inventory' }, { label: 'Stock' }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={load}><FiRefreshCw /> Refresh</button>
            <button className="btn btn-primary" onClick={openAdjust}><FiEdit3 /> Stock Adjustment</button>
            <button className="btn btn-success" onClick={() => exportToExcel(rows, 'stock_report', cols)} disabled={!rows.length}>Export Excel</button>
          </>
        }
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input placeholder="Search article/name..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          {loading ? <Loader text="Loading stock..." /> : (
            <DataTable data={rows.filter((r) => !search || String(r.article_no || '').toLowerCase().includes(search.toLowerCase()) || String(r.name || '').toLowerCase().includes(search.toLowerCase()))} columns={cols} emptyTitle="No stock found" />
          )}
        </div>
      </div>

      {showAdj && (
        <div className="modal-overlay" onClick={() => setShowAdj(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Stock Adjustment</h3>
              <button className="modal-close" onClick={() => setShowAdj(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="grid-3">
                <div className="form-group"><label className="form-label">Type <span className="req">*</span></label>
                  <SearchableSelect
                    options={[{ value: 'IN', label: 'IN — Add Stock' }, { value: 'OUT', label: 'OUT — Remove Stock' }, { value: 'ADJUST', label: 'ADJUST — Set Balance' }]}
                    value={adjForm.type} onChange={(v) => setAdj('type', v)} isInvalid={!!adjErrors.type} placeholder="-- Select --"
                  />
                  {adjErrors.type && <div className="invalid-feedback">{adjErrors.type}</div>}</div>
                <div className="form-group"><label className="form-label">Article No <span className="req">*</span></label>
                  <input className={`form-control ${adjErrors.article_no ? 'is-invalid' : ''}`} value={adjForm.article_no} onChange={(e) => setAdj('article_no', e.target.value)} placeholder="ART-001" />
                  {adjErrors.article_no && <div className="invalid-feedback">{adjErrors.article_no}</div>}</div>
                <div className="form-group"><label className="form-label">Name <span className="req">*</span></label>
                  <input className={`form-control ${adjErrors.name ? 'is-invalid' : ''}`} value={adjForm.name} onChange={(e) => setAdj('name', e.target.value)} placeholder="Cotton Fabric" />
                  {adjErrors.name && <div className="invalid-feedback">{adjErrors.name}</div>}</div>
              </div>
              <div className="grid-3">
                <div className="form-group"><label className="form-label">Unit <span className="req">*</span></label>
                  <SearchableSelect options={units.map((u) => ({ value: u.short_name || u.name, label: u.short_name ? u.name + ' (' + u.short_name + ')' : u.name }))} value={adjForm.unit} onChange={(v) => setAdj('unit', v)} isInvalid={!!adjErrors.unit} placeholder="-- Select --" />
                  {adjErrors.unit && <div className="invalid-feedback">{adjErrors.unit}</div>}</div>
                <div className="form-group"><label className="form-label">Quantity <span className="req">*</span></label>
                  <input className={`form-control ${adjErrors.quantity ? 'is-invalid' : ''}`} type="number" value={adjForm.quantity} onChange={(e) => setAdj('quantity', e.target.value)} placeholder="0" />
                  {adjErrors.quantity && <div className="invalid-feedback">{adjErrors.quantity}</div>}</div>
                <div className="form-group"><label className="form-label">Date <span className="req">*</span></label>
                  <input className={`form-control ${adjErrors.date ? 'is-invalid' : ''}`} type="date" value={adjForm.date} onChange={(e) => setAdj('date', e.target.value)} />
                  {adjErrors.date && <div className="invalid-feedback">{adjErrors.date}</div>}</div>
              </div>
              <div className="form-group"><label className="form-label">Notes</label>
                <textarea className="form-control" value={adjForm.notes} onChange={(e) => setAdj('notes', e.target.value)} placeholder="Optional notes..." /></div>
              {adjForm.type === 'ADJUST' && (
                <div className="alert alert-info" style={{ marginTop: 8 }}>
                  <strong>ADJUST</strong> sets the exact final balance. The system will auto-calculate IN/OUT difference.
                </div>
              )}
              {adjForm.type === 'OUT' && (
                <div className="alert alert-warning" style={{ marginTop: 8 }}>
                  <strong>OUT</strong> will be blocked if stock would go negative.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAdj(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdjust} disabled={saving}>{saving ? 'Saving...' : 'Apply Adjustment'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}